import { createContext, useContext, useEffect, useState } from "react";
import i18n from "@/lib/i18n";
import {
  getLanguageFromCookie,
  setLanguageCookie,
  getThemeFromCookie,
  setThemeCookie,
  getSidebarCollapsedFromCookie,
  setSidebarCollapsedCookie,
} from "@/lib/cookies";

type Theme = "light" | "dark" | "system";
type SupportedLanguage = "en" | "es" | "sr";

interface PreferenceContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  singleLanguageMode: boolean;
  singleThemeMode: boolean;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const PreferenceContext = createContext<PreferenceContextType | undefined>(undefined);

interface PreferenceProviderProps {
  children: React.ReactNode;
  singleLanguage?: SupportedLanguage;
  singleTheme?: Theme;
}

export function PreferenceProvider({ children, singleLanguage, singleTheme }: PreferenceProviderProps) {
  // Theme state - use singleTheme if provided, otherwise cookie/default
  const [theme, setThemeState] = useState<Theme>(() => {
    if (singleTheme) return singleTheme;
    return getThemeFromCookie();
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  const singleThemeMode = !!singleTheme;

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;
    const targetTheme = singleTheme || theme;

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add("dark");
        setResolvedTheme("dark");
      } else {
        root.classList.remove("dark");
        setResolvedTheme("light");
      }
    };

    if (targetTheme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      applyTheme(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else {
      applyTheme(targetTheme === "dark");
    }
  }, [theme, singleTheme]);

  // Theme setter - only works if not in single theme mode
  const setTheme = (newTheme: Theme) => {
    if (singleThemeMode) return;
    setThemeState(newTheme);
    setThemeCookie(newTheme);
  };

  // Language state - use singleLanguage if provided, otherwise cookie/default
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (singleLanguage) return singleLanguage;
    return getLanguageFromCookie();
  });

  const singleLanguageMode = !!singleLanguage;

  // Sync i18n when language changes. The dispatch module generates its data
  // in the active language, so its cached base has to be dropped too.
  useEffect(() => {
    const targetLang = singleLanguage || language;
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
      void import('@/api/dispatch/store').then((store) => store.invalidateBase());
    }
  }, [language, singleLanguage]);

  // Language setter - only works if not in single language mode
  const setLanguage = (lang: SupportedLanguage) => {
    if (singleLanguageMode) return;
    setLanguageState(lang);
    setLanguageCookie(lang);
    i18n.changeLanguage(lang);
    void import('@/api/dispatch/store').then((store) => store.invalidateBase());
  };

  // Sidebar collapsed state - persisted in cookie
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    return getSidebarCollapsedFromCookie();
  });

  // Sidebar setter - updates state and cookie
  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    setSidebarCollapsedCookie(collapsed);
  };

  return (
    <PreferenceContext.Provider value={{ theme, setTheme, resolvedTheme, language, setLanguage, singleLanguageMode, singleThemeMode, sidebarCollapsed, setSidebarCollapsed }}>
      {children}
    </PreferenceContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferenceContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PreferenceProvider");
  }
  return context;
}
